import { styled } from '@mui/material/styles';
import type { NavRouteConfig, RouterMethod } from '@openx/utils/routes/navTypes.ts';
import { type JSX, type MouseEvent, useCallback } from 'react';
import { useLocation } from 'react-router';

import { NavLinkWrapper } from './NavLinkWrapper.tsx';

export interface MenuPositionProps extends Omit<NavRouteConfig, 'permissionCheck'> {
  activeIndicatorEnabled?: boolean;
  onClick?: (path: string, method: RouterMethod) => void;
  Icon?: () => JSX.Element | null;
}

const StyledNavLink = styled(NavLinkWrapper)`
  border-color: transparent;
  border-left-style: solid;
  border-left-width: 3px;
  color: ${({ theme }) => theme.palette.text.primary};
  display: block;
  font-size: 14px;
  left: ${({ theme }) => theme.spacing(-4)};
  padding-left: calc(${({ theme }) => theme.spacing(4)} - 3px);
  line-height: 28px;

  &:hover {
    background: ${({ theme }) => theme.palette.background.boxDark};
  }

  &.active {
    border-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.primary.main} !important;
  }

  &:visited {
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

/**
 * @deprecated - Please use the {@link SideNavItem} instead.
 */
export function MenuItem({
  path,
  title,
  activeIndicatorEnabled = false,
  onClick,
  method,
  Icon,
  activePathOverride,
}: MenuPositionProps): JSX.Element {
  const location = useLocation();

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      e.preventDefault();
      onClick(path, method);
    }
  };

  const isActiveOverride = useCallback(() => {
    if (!activePathOverride) {
      return;
    }

    return location.pathname.includes(activePathOverride);
  }, [activePathOverride, location.pathname]);

  return (
    <StyledNavLink
      onClick={handleClick}
      activeIndicatorEnabled={activeIndicatorEnabled}
      overrideIsActive={isActiveOverride()}
      to={path}
      data-test="menu-item"
    >
      {title}
      {Icon && <Icon />}
    </StyledNavLink>
  );
}
